import { inject, Injectable } from '@angular/core';
import { Agent } from '@ag-common-lib/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { combineLatest, from, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AgentChangesService } from './agent-changes.service';
import { AgentSearchService } from './agent-search.service';

/**
 * The agents a screen renders, keyed by dbId.
 *
 * Screens used to take `AgentService.agentsMap$` — every agent in the collection, built
 * eagerly — and index into it per row. This answers the same question from the ids the
 * screen actually has, so a grid page costs one request for its own rows rather than a
 * read of the whole directory.
 *
 * The difference that matters to a caller: the old map held everyone, so a lookup only
 * missed when the agent did not exist. This one holds what was asked for, so a lookup
 * misses when the id was never passed in. Feed it every id the template will index by,
 * not just the ones it displays.
 */
@Injectable({ providedIn: 'root' })
export class AgentsMapService {
  private readonly agentSearchService = inject(AgentSearchService);
  private readonly agentChangesService = inject(AgentChangesService);

  /**
   * Re-reads on two triggers: the id set changing, and the projection publishing a change.
   *
   * The signal is needed because `getByIds` is uncached — an edit elsewhere would
   * otherwise leave a rendered name stale for as long as the screen stays open. It is the
   * debounced any-change stream rather than the per-agent one on purpose: deciding whether
   * a named agent is on the page means tracking rendered keys, which is the bookkeeping
   * this exists to avoid.
   */
  byDbIds(dbIds$: Observable<string[]>): Observable<Record<string, Agent>> {
    const distinctIds$ = dbIds$.pipe(
      map(dbIds => Array.from(new Set((dbIds ?? []).filter(Boolean))).sort()),
      distinctUntilChanged(
        (previous, current) => previous.length === current.length && previous.every((id, index) => id === current[index]),
      ),
    );

    return combineLatest([distinctIds$, this.agentChangesService.agentsChanged$.pipe(startWith(undefined))]).pipe(
      switchMap(([dbIds]) => (dbIds.length ? from(this.agentSearchService.getByIds(dbIds)) : of<Agent[]>([]))),
      map(agents => {
        const agentsMap: Record<string, Agent> = {};

        agents.forEach(agent => {
          const agentDbId = agent?.[BaseModelKeys.dbId];

          if (agentDbId) {
            agentsMap[agentDbId] = agent;
          }
        });

        return agentsMap;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
